import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, Send, ScanSearch } from "lucide-react";
import { getAeoConfig, saveAeoConfig, detectAeoSchema, runAeoDispatch, getAeoRuns } from "@/lib/rexxponsApi";

const input = "w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary";
const STATUS = {
  success: "bg-primary/15 text-primary",
  warning: "bg-amber-500/15 text-amber-500",
  failed: "bg-destructive/15 text-destructive",
  skipped: "bg-secondary text-muted-foreground",
};

const Section = ({ title, children }) => (
  <div className="bg-card border border-border rounded-xl p-5 space-y-3">
    <p className="text-xs tracking-[0.16em] uppercase text-muted-foreground">{title}</p>
    {children}
  </div>
);

const ResultRow = ({ r, i }) => (
  <div data-testid={`aeo-result-${i}`} className="border border-border rounded-lg p-3 text-sm">
    <div className="flex items-center justify-between gap-3">
      <span className="text-foreground break-all"><b className="uppercase text-xs mr-2">{r.channel}</b>{r.target}</span>
      <span data-testid={`aeo-result-status-${i}`} className={`text-[10px] uppercase tracking-[0.14em] rounded-full px-2.5 py-1 shrink-0 ${STATUS[r.status]}`}>{r.status}</span>
    </div>
    <p className="text-xs text-muted-foreground mt-1">{r.message}</p>
    {r.fallback_jsonld && (
      <pre data-testid="aeo-fallback-jsonld" className="mt-2 text-[11px] bg-background border border-border rounded p-2 overflow-x-auto">{JSON.stringify(r.fallback_jsonld, null, 2)}</pre>
    )}
  </div>
);

const RunResult = ({ run }) => (
  <div className="space-y-2" data-testid="aeo-run-result">
    <div className="flex items-baseline gap-3">
      <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Rexx Index Reliability</span>
      <span data-testid="aeo-reliability" className="font-heading text-3xl text-primary">{run.reliability == null ? "—" : `${run.reliability}%`}</span>
    </div>
    <p className="text-xs text-muted-foreground" data-testid="aeo-run-counts">{run.executed} executed · {run.skipped} skipped (not counted)</p>
    {run.results.map((r, i) => <ResultRow key={i} r={r} i={i} />)}
  </div>
);

const WebhookList = ({ hooks, setHooks }) => {
  const upd = (i, k, v) => setHooks(hooks.map((h, j) => (j === i ? { ...h, [k]: v } : h)));
  return (
    <div className="space-y-2">
      {hooks.map((h, i) => (
        <div key={i} className="flex gap-2 items-center">
          <select data-testid={`aeo-webhook-provider-${i}`} value={h.provider} onChange={(e) => upd(i, "provider", e.target.value)} className={`${input} w-28`}>
            <option value="make">Make</option><option value="zapier">Zapier</option>
          </select>
          <input data-testid={`aeo-webhook-name-${i}`} value={h.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Name" className={`${input} w-36`} />
          <input data-testid={`aeo-webhook-url-${i}`} value={h.url} onChange={(e) => upd(i, "url", e.target.value)} placeholder="https://hook.eu1.make.com/..." className={input} />
          <button data-testid={`aeo-webhook-remove-${i}`} onClick={() => setHooks(hooks.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button data-testid="aeo-webhook-add" onClick={() => setHooks([...hooks, { provider: "make", name: "", url: "" }])} className="flex items-center gap-1.5 text-xs text-primary"><Plus className="w-3.5 h-3.5" /> Add webhook</button>
    </div>
  );
};

export const AeoPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [key, setKey] = useState("");
  const [hooks, setHooks] = useState([]);
  const [urls, setUrls] = useState("");
  const [detectUrl, setDetectUrl] = useState("");
  const [busy, setBusy] = useState("");
  const [detect, setDetect] = useState(null);
  const [run, setRun] = useState(null);

  const apply = (c) => { setCfg(c); setHooks(c.webhooks || []); };
  useEffect(() => {
    getAeoConfig().then(apply).catch(() => toast.error("Could not load AEO config"));
    getAeoRuns().then((r) => r[0] && setRun(r[0])).catch(() => {});
  }, []);

  const wrap = (name, fn) => async () => {
    setBusy(name);
    try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); } finally { setBusy(""); }
  };
  const save = wrap("save", async () => {
    apply(await saveAeoConfig({ ...cfg, webhooks: hooks, indexnow_key: key ? key : undefined }));
    setKey("");
    toast.success("AEO configuration saved");
  });
  const clearKey = wrap("clear", async () => {
    apply(await saveAeoConfig({ ...cfg, webhooks: hooks, indexnow_key: "" }));
    toast.success("IndexNow key removed");
  });
  const doDetect = wrap("detect", async () => setDetect(await detectAeoSchema(detectUrl.trim())));
  const doDispatch = wrap("dispatch", async () => setRun(await runAeoDispatch(urls.split(/\s+/).filter(Boolean))));

  if (!cfg) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  const spin = (n, Icon) => (busy === n ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />);
  const btn = "flex items-center gap-1.5 text-xs bg-primary text-primary-foreground rounded-full px-4 py-2 disabled:opacity-50";

  return (
    <div className="space-y-4" data-testid="aeo-panel">
      <Section title="Configuration">
        <input data-testid="aeo-site-url" value={cfg.site_url} onChange={(e) => setCfg({ ...cfg, site_url: e.target.value })} placeholder="Site URL (https://nexxaris.com)" className={input} />
        <input data-testid="aeo-indexnow-key" value={key} onChange={(e) => setKey(e.target.value)} placeholder={cfg.indexnow_key_set ? `IndexNow key saved (${cfg.indexnow_key_masked}) — type to replace` : "IndexNow API key"} className={input} />
        {cfg.indexnow_key_set && (
          <button data-testid="aeo-indexnow-clear" onClick={clearKey} disabled={!!busy} className="text-xs text-muted-foreground hover:text-destructive">Remove saved IndexNow key</button>
        )}
        <input data-testid="aeo-indexnow-key-location" value={cfg.indexnow_key_location} onChange={(e) => setCfg({ ...cfg, indexnow_key_location: e.target.value })} placeholder="Key file URL (optional, default https://host/KEY.txt)" className={input} />
        <WebhookList hooks={hooks} setHooks={setHooks} />
        <button data-testid="aeo-save-btn" onClick={save} disabled={!!busy} className={btn}>{spin("save", Save)} Save</button>
      </Section>
      <Section title="Schema.org Detect">
        <div className="flex gap-2">
          <input data-testid="aeo-detect-url" value={detectUrl} onChange={(e) => setDetectUrl(e.target.value)} placeholder="https://example.com/page" className={input} />
          <button data-testid="aeo-detect-btn" onClick={doDetect} disabled={!!busy || !detectUrl.trim()} className={btn}>{spin("detect", ScanSearch)} Detect</button>
        </div>
        {detect && <ResultRow r={detect} i="detect" />}
      </Section>
      <Section title="Dispatch">
        <textarea data-testid="aeo-dispatch-urls" value={urls} onChange={(e) => setUrls(e.target.value)} rows={3} placeholder="One URL per line" className={input} />
        <button data-testid="aeo-dispatch-btn" onClick={doDispatch} disabled={!!busy || !urls.trim()} className={btn}>{spin("dispatch", Send)} Run dispatch</button>
        {run && <RunResult run={run} />}
      </Section>
    </div>
  );
};
